import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../components/CustomButton';
import CancelAccountDialog from './CancelAccountDialog';
import {
    useCancelAccountMutation,
    useGetAccountCancellationQuery,
    useRequestAccountDeletionMutation,
} from '../../api/account/accountApiSlice';
import { useSignOut } from '../../hooks/useSignOut';
import { errorMessageFrom } from '../../utils/errorMessage';
import { ARCHIVE_RETENTION_DAYS } from '../../utils/tenantArchived';

interface CancelAccountSectionProps {
    /**
     * #1379: an operational-DB tenant's "Cancel account" archives at once; a
     * legacy tenant (own database) keeps the deletion request. The page passes
     * its existing `isSelfService` distinction.
     */
    archive: boolean;
    /** The name the owner must type to confirm. */
    organizationName: string;
}

/**
 * The Account page's "Cancel account" unit: the section with its button and
 * the confirmation dialog. An action, not a form, so it keeps its own button
 * rather than the "Bearbeiten" flow. Moved out of AccountPage with #1379.
 *
 * - archive: the upcoming bookings are read each time the dialog opens; on
 *   success the owner is signed out, because the account is closed.
 * - request ("Löschung beantragen"): success is shown only once the server
 *   confirmed both mails went out.
 *
 * A failure stays in the dialog with its reason in both flows.
 */
const CancelAccountSection = ({ archive, organizationName }: CancelAccountSectionProps) => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [requested, setRequested] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [requestAccountDeletion, { isLoading: requestSending }] = useRequestAccountDeletionMutation();
    const [cancelAccount, { isLoading: cancelSending }] = useCancelAccountMutation();
    const signOut = useSignOut();
    // Read each time the dialog opens, never from an older visit.
    const cancellation = useGetAccountCancellationQuery(undefined, {
        skip: !open || !archive,
        refetchOnMountOrArgChange: true,
    });

    const handleConfirm = async () => {
        setError(null);
        const language = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'de';
        if (archive) {
            try {
                await cancelAccount({ language }).unwrap();
                signOut();
            } catch (err) {
                setError(errorMessageFrom(err, t('account.archive_error'), t));
            }
            return;
        }
        try {
            await requestAccountDeletion({ language }).unwrap();
            setRequested(true);
        } catch (err) {
            setError(errorMessageFrom(err, t('account.cancel_error'), t));
        }
    };

    const close = () => {
        setOpen(false);
        setRequested(false);
        setError(null);
    };

    const labels = archive
        ? {
            title: t('account.archive_section_title'),
            description: t('account.archive_description', { days: ARCHIVE_RETENTION_DAYS }),
            button: t('account.archive_button'),
        }
        : {
            title: t('account.danger_zone'),
            description: t('account.cancel_description'),
            button: t('account.cancel_account'),
        };

    return (
        <>
            {/* Neutral framing; the confirmation dialog carries the destructive accents. */}
            <section className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
                <h2 className={'text-lg font-semibold text-foreground mb-2'}>{labels.title}</h2>
                <p className={'text-sm text-foreground/60 mb-5'}>{labels.description}</p>
                <CustomButton title={labels.button} onClick={() => setOpen(true)} isFilled={false} />
            </section>
            {open && (
                <CancelAccountDialog
                    mode={archive ? 'archive' : 'request'}
                    organizationName={organizationName}
                    onConfirm={handleConfirm}
                    onClose={close}
                    submitting={archive ? cancelSending : requestSending}
                    submitted={requested}
                    error={error}
                    upcoming={archive ? {
                        loading: cancellation.isFetching,
                        failed: cancellation.isError,
                        count: cancellation.data?.upcomingBookings ?? null,
                    } : undefined}
                    retentionDays={cancellation.data?.retentionDays}
                />
            )}
        </>
    );
};

export default CancelAccountSection;
